import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Flag } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Pill, inputCls } from "@/components/hub/Common";
import { fmtDate } from "@/components/challenges/ChallengeHero";

const local = (d) => { const x = new Date(d); x.setMinutes(x.getMinutes() - x.getTimezoneOffset()); return x.toISOString().slice(0, 16); };
const week = () => {
  const s = new Date(); s.setHours(0, 0, 0, 0); s.setDate(s.getDate() - ((s.getDay() + 6) % 7));
  const e = new Date(s); e.setDate(e.getDate() + 6); e.setHours(23, 59, 0, 0);
  return { title: "", description: "", game: "", starts_at: local(s), ends_at: local(e) };
};

const Form = ({ initial, id, onDone }) => {
  const { t } = useI18n();
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...f, starts_at: new Date(f.starts_at).toISOString(), ends_at: new Date(f.ends_at).toISOString() };
    try { if (id) await api.put(`/admin/challenges/${id}`, body); else await api.post("/admin/challenges", body); toast.success(t("adm.ch_created")); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <form onSubmit={submit} className="cx-card cx-brackets rounded-2xl p-6 space-y-3" data-testid="challenge-form">
      <div className="font-display text-lg text-white">{id ? t("adm.ch_edit") : t("adm.ch_new")}</div>
      <div className="grid sm:grid-cols-[1fr_220px] gap-3">
        <input data-testid="challenge-title-input" className={inputCls} placeholder={t("adm.ch_title")} value={f.title} onChange={set("title")} required minLength={3} maxLength={80} />
        <input data-testid="challenge-game-input" className={inputCls} placeholder={t("adm.ch_game")} value={f.game} onChange={set("game")} maxLength={40} />
      </div>
      <textarea data-testid="challenge-desc-input" className={`${inputCls} min-h-[80px]`} placeholder={t("adm.ch_desc")} value={f.description} onChange={set("description")} maxLength={600} />
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-400 space-y-1"><span>{t("adm.ch_start")}</span><input type="datetime-local" data-testid="challenge-start-input" className={inputCls} value={f.starts_at} onChange={set("starts_at")} required /></label>
        <label className="text-xs text-slate-400 space-y-1"><span>{t("adm.ch_end")}</span><input type="datetime-local" data-testid="challenge-end-input" className={inputCls} value={f.ends_at} onChange={set("ends_at")} required /></label>
      </div>
      <div className="flex gap-2 pt-1">
        <button className="cx-btn rounded-full px-6 py-2.5 text-sm font-semibold" data-testid="challenge-save-btn">{t("adm.ch_save")}</button>
        <button type="button" onClick={onDone} className="cx-ghost rounded-full px-5 py-2.5 text-sm text-slate-300" data-testid="challenge-cancel-btn">{t("adm.ch_cancel")}</button>
      </div>
    </form>
  );
};

const TONE = { active: "rose", upcoming: "amber", ended: "slate" };

const Row = ({ c, onEdit, reload }) => {
  const { t, lang } = useI18n();
  const act = async (fn, msg, confirmKey) => {
    if (!window.confirm(t(confirmKey))) return;
    try { await fn(); toast.success(t(msg)); reload(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="cx-card rounded-2xl p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`admin-challenge-${c.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2"><Pill tone={TONE[c.status]}>{t(`ch.${c.status}`)}</Pill><span className="text-white font-semibold truncate">{c.title}</span>{c.game && <span className="text-xs text-slate-500">{c.game}</span>}</div>
        <div className="text-xs text-slate-500 mt-1.5 font-mono">{fmtDate(c.starts_at, lang)} → {fmtDate(c.ends_at, lang)} · {t((c.entries) === 1 ? "ch.entries_one" : "ch.entries").replace("{n}", c.entries)}</div>
        <div className="text-xs mt-1.5" data-testid={`admin-challenge-leader-${c.id}`}>
          {c.winner ? <span className="text-[#ffc94d]">{t("ch.winner")} : {c.winner.creator} · {t((c.winner.score) === 1 ? "ch.score_one" : "ch.score").replace("{n}", c.winner.score)}</span>
            : c.settled ? <span className="text-slate-500">{t("ch.no_winner")}</span>
              : c.leader && <span className="text-slate-300">{t("adm.ch_leader")} : {c.leader.creator} · {t((c.leader.score) === 1 ? "ch.score_one" : "ch.score").replace("{n}", c.leader.score)}</span>}
        </div>
      </div>
      <div className="flex gap-2">
        {!c.settled && <button onClick={() => onEdit(c)} className="cx-ghost rounded-lg px-3 py-2 text-xs text-slate-300 flex items-center gap-1.5" data-testid={`admin-challenge-edit-${c.id}`}><Pencil className="w-3.5 h-3.5" /></button>}
        {!c.settled && c.status !== "upcoming" && <button onClick={() => act(() => api.post(`/admin/challenges/${c.id}/close`), "adm.ch_closed", "adm.ch_close_confirm")} className="rounded-lg bg-[#ffc94d]/10 border border-[#ffc94d]/40 text-[#ffc94d] px-3 py-2 text-xs flex items-center gap-1.5" data-testid={`admin-challenge-close-${c.id}`}><Flag className="w-3.5 h-3.5" />{t("adm.ch_close")}</button>}
        {!c.winner && <button onClick={() => act(() => api.delete(`/admin/challenges/${c.id}`), "adm.ch_deleted", "adm.ch_delete_confirm")} className="rounded-lg bg-rose-500/10 border border-rose-400/30 text-rose-300 px-3 py-2 text-xs" data-testid={`admin-challenge-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
      </div>
    </div>
  );
};

export const ChallengesTab = () => {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = useCallback(() => api.get("/admin/challenges").then((r) => setList(r.data)).catch(() => setList([])), []);
  useEffect(() => { load(); }, [load]);
  const done = () => { setForm(null); load(); };
  return (
    <div className="space-y-5" data-testid="admin-challenges">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-400 max-w-2xl">{t("adm.ch_intro")}</p>
        {!form && <button onClick={() => setForm({ init: week() })} className="cx-btn rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 shrink-0" data-testid="admin-challenge-new-btn"><Plus className="w-4 h-4" />{t("adm.ch_new")}</button>}
      </div>
      {form && <Form initial={form.init} id={form.id} onDone={done} />}
      {!list.length && !form && <p className="text-sm text-slate-500 py-10 text-center" data-testid="admin-challenges-empty">{t("adm.ch_empty")}</p>}
      {list.map((c) => <Row key={c.id} c={c} reload={load}
        onEdit={(x) => setForm({ id: x.id, init: { title: x.title, description: x.description, game: x.game, starts_at: local(x.starts_at), ends_at: local(x.ends_at) } })} />)}
    </div>
  );
};
